/* =============================================================================
   [KI] Mechanism — pinned scrollytelling controller
   -----------------------------------------------------------------------------
   The section is tall (340vh). Its child .mechanism__stage is sticky — pinned
   to viewport top for the full section scroll. We measure scroll progress
   through the section and derive:

     - overall progress p ∈ [0, 1]
     - active beat: 0 | 1 | 2
     - per-strand --q (lattice draw-in)

   Progress bands:
     p ∈ [0.00, 0.40)  → Beat 0, Phase 1 (warp scaffold draws)
     p ∈ [0.40, 0.80)  → Beat 1, Phase 2 (weft scaffold draws)
     p ∈ [0.80, 1.00]  → Beat 2, Phase 3 (infill draws)

   Phase 1 completes when the section bottom enters the viewport — i.e. at
   the moment the pin is about to release into Beat 2.
   ========================================================================== */

(function () {
  'use strict';

  // Progress → beat thresholds. Phase 1: 0–40%, Phase 2: 40–80%, Phase 3: 80–100%.
  var BEAT_EDGES = [0.40, 0.80]; // p < 0.40 → 0; p < 0.80 → 1; else 2

  // Lattice phase windows (progress space 0..1). infill span trimmed to 0.14 so
  // its strands still spread smoothly across the narrower 0.80–1.00 finale.
  var PHASE = {
    warpScaffold: { start: 0.00, end: 0.40, span: 0.18 },
    weftScaffold: { start: 0.40, end: 0.80, span: 0.14 },
    infill:       { start: 0.80, end: 1.00, span: 0.14 }
  };

  function initMechanism(root) {
    if (!root || root.__mcInit) return;
    root.__mcInit = true;

    var stage   = root.querySelector('.mechanism__stage');
    var beats   = Array.prototype.slice.call(root.querySelectorAll('.mechanism__beat'));
    var strands = Array.prototype.slice.call(root.querySelectorAll('.mc-strand'));
    // Queried early so writeProgress() (defined below) can always see it,
    // regardless of which code path — desktop/mobile/reduced-motion —
    // triggers the first write.
    var lattice = root.querySelector('.mechanism__lattice');
    if (!stage || !strands.length) return;

    // ── Per-strand activation windows ───────────────────────────────────
    var warpS = strands.filter(function (s) { return s.classList.contains('mc-strand--warp-scaffold'); });
    var weftS = strands.filter(function (s) { return s.classList.contains('mc-strand--weft-scaffold'); });
    var warpI = strands.filter(function (s) { return s.classList.contains('mc-strand--warp-infill'); });
    var weftI = strands.filter(function (s) { return s.classList.contains('mc-strand--weft-infill'); });

    function assignWindows(group, spec) {
      var n = group.length;
      if (!n) return;
      var usable = Math.max(0.001, (spec.end - spec.span) - spec.start);
      // Small deterministic jitter per index so strands don't fire on a
      // perfectly-uniform beat. Range: ±~12% of the per-strand step.
      var step = n > 1 ? (1 / (n - 1)) : 0;
      var jitterAmp = step * 0.24;
      group.forEach(function (el, i) {
        var t = n > 1 ? (i / (n - 1)) : 0;
        // Deterministic pseudo-random in [-0.5, 0.5] from index
        var h = Math.sin((i + 1) * 12.9898) * 43758.5453;
        var r = (h - Math.floor(h)) - 0.5;
        var jitter = r * jitterAmp;
        var tj = Math.max(0, Math.min(1, t + jitter));
        var start = spec.start + tj * usable;
        el.__start = start;
        el.__end = start + spec.span;
      });
    }

    assignWindows(warpS, PHASE.warpScaffold);
    assignWindows(weftS, PHASE.weftScaffold);

    // Merge warp-infill + weft-infill alternating so density climbs uniformly
    var infillMerged = [];
    var maxLen = Math.max(warpI.length, weftI.length);
    for (var k = 0; k < maxLen; k++) {
      if (warpI[k]) infillMerged.push(warpI[k]);
      if (weftI[k]) infillMerged.push(weftI[k]);
    }
    assignWindows(infillMerged, PHASE.infill);

    // ── Phase-week readout windows (mobile) ─────────────────────────────
    // Each "WOCHE …" label is scrubbed continuously from progress (see
    // writeReadout) so it rises in from below, holds at centre during its
    // phase, then PEELS OFF and dies upward into the lattice as the next
    // phase takes over — instead of snapping at the discrete beat boundary.
    var weekLabels = Array.prototype.slice.call(root.querySelectorAll('.mechanism__phase-week'));
    // Timing is anchored to each phase's MIDPOINT (where the label is centred +
    // full opacity). It fades/rises in over RO_RISE_SPAN before the midpoint, then
    // begins a slow upward death over RO_FALL_SPAN — which starts AT the midpoint,
    // i.e. preemptively, well before the phase box ends, so the outgoing label has
    // already drifted up and out of the centre before the next one fades in (no
    // centre overlap). RO_FALL_SPAN is deliberately long (~4× the old window = ¼
    // the speed); the label lingering outside its phase box during this slow,
    // constant-pace decay is intended.
    var RO_RISE_SPAN = 0.12;   // fade/rise-in window before the anchor (progress)
    var RO_FALL_SPAN = 0.72;   // slow upward DRIFT window after the anchor (progress)
    var RO_FADE_SPAN = 0.28;   // OPACITY fade-out window after the anchor — shorter than
                               // the drift so the dying label is gone before the next one
                               // lands, even though it keeps drifting up (invisibly).
    var RO_RISE = 12;          // px the incoming label rises from below as it fades in
    // The LAST label is special: it's the terminal readout, so it does NOT peel
    // off and die. It has NO vertical movement — just a slow, scroll-linked
    // opacity fade-in that reaches full only near the end of the reveal (when the
    // lattice is fully dense). These two progress points define that fade.
    var RO_LAST_FADE_START = 0.80; // progress where the final label begins to appear
    var RO_LAST_FADE_FULL  = 0.96; // progress where it reaches full opacity (then holds)
    // Death travel is a FRACTION of the canvas (lattice) height, not a fixed px,
    // so it peels ~50% of the canvas (centre → top) regardless of lattice size.
    var RO_DIE_FRAC = 0.5;
    var canvasH = 0; // live lattice height; refreshed each mobile frame (computeMobileProgress)
    function measureCanvas() {
      var host = lattice || scene;
      if (host) { var h = host.getBoundingClientRect().height; if (h > 0) canvasH = h; }
    }
    measureCanvas();
    var weekWindows = [
      { s: 0,             e: BEAT_EDGES[0] },
      { s: BEAT_EDGES[0], e: BEAT_EDGES[1] },
      { s: BEAT_EDGES[1], e: 1 }
    ];
    weekLabels.forEach(function (el) {
      var ph = parseInt(el.getAttribute('data-phase') || '0', 10);
      var w = weekWindows[ph] || weekWindows[0];
      var m = (w.s + w.e) / 2;     // anchor: phase midpoint (centred + full opacity here)
      el.__last = (ph === weekWindows.length - 1); // terminal label → fade-in only (no death)
      el.__m  = m;
      el.__rs = m - RO_RISE_SPAN;   // rise-in start
      el.__fe = m + RO_FALL_SPAN;   // death end
      // JS scrubs opacity/transform every frame — kill the CSS transition so the
      // label tracks scroll exactly (the data-beat CSS rules remain as a no-JS
      // fallback when this controller doesn't run).
      el.style.transition = 'none';
    });

    // Ease-out-cubic
    function ease(t) {
      var u = 1 - t;
      return 1 - u * u * u;
    }

    // Continuous phase-week scrub: rise-in → hold → peel-off-and-die (upward).
    // Driven from the same progress p as the lattice, so a week visibly lifts up
    // and dissolves into the grid as you scroll, rather than blinking out.
    function writeReadout(p) {
      var dieDist = RO_DIE_FRAC * (canvasH || 600); // px from centre toward the top
      for (var wi = 0; wi < weekLabels.length; wi++) {
        var el = weekLabels[wi];
        var y, o;
        if (el.__last) {
          // Terminal label: NO vertical movement — a slow, scroll-linked opacity
          // fade-in that only reaches full near the end of the reveal, then holds.
          var tl = (p - RO_LAST_FADE_START) / Math.max(0.001, RO_LAST_FADE_FULL - RO_LAST_FADE_START);
          o = tl <= 0 ? 0 : (tl >= 1 ? 1 : tl);
          y = 0;
        } else if (p < el.__rs) {
          // Not yet — waiting just below centre, hidden.
          y = RO_RISE; o = 0;
        } else if (p < el.__m) {
          // Fading + rising into centre (a clear fade-in, not a pop).
          var tin = (p - el.__rs) / Math.max(0.001, el.__m - el.__rs);
          var tine = ease(tin);
          y = RO_RISE * (1 - tine);
          o = tine;
        } else if (p < el.__fe) {
          // Upward death begins AT the midpoint — preemptively, before the phase
          // box ends — so the centre is clear by the time the next label fades in.
          // Position drifts slowly over the full fall span; OPACITY fades over the
          // shorter RO_FADE_SPAN so the label disappears promptly rather than
          // hanging around faintly while it keeps drifting up.
          var t = (p - el.__m) / Math.max(0.001, el.__fe - el.__m);
          y = -dieDist * t;
          var tf = (p - el.__m) / RO_FADE_SPAN;
          o = tf >= 1 ? 0 : 1 - tf;
        } else {
          y = -dieDist; o = 0;
        }
        el.style.opacity = o.toFixed(3);
        el.style.transform = 'translateY(' + y.toFixed(1) + 'px)';
      }
    }

    // ── Progress write ──────────────────────────────────────────────────
    var lastProgress = -1;
    var lastBeat = -1;

    function writeProgress(p) {
      if (p < 0) p = 0; else if (p > 1) p = 1;

      // Completion signal — always up-to-date even when the progress
      // short-circuit below skips the rest of the write. Without this
      // the attribute can stay stale at the reveal boundary because the
      // final p diff (0.998 → 1.0) is below the 0.002 cutoff.
      if (p >= 0.999) {
        root.setAttribute('data-reveal-complete', '');
        // Inline-style override: kill the feather mask + clip directly
        // on the element. Inline styles beat any stylesheet rule
        // regardless of specificity or media-query nesting.
        //
        // NOTE: we do NOT clear the transform here. The terminal
        // transform (translateY(3.5%) + scale(1.0)) IS the rest state
        // of the parallax animation — snapping it to `none` would jolt
        // the lattice back up to y=0 at the moment of completion.
        if (lattice) {
          lattice.style.webkitMaskImage = 'none';
          lattice.style.maskImage = 'none';
          lattice.style.clipPath = 'none';
        }
      } else if (root.hasAttribute('data-reveal-complete')) {
        root.removeAttribute('data-reveal-complete');
        // Restore CSS control when we drop out of completion state
        if (lattice) {
          lattice.style.webkitMaskImage = '';
          lattice.style.maskImage = '';
          lattice.style.clipPath = '';
        }
      }

      if (Math.abs(p - lastProgress) < 0.002) return;
      lastProgress = p;

      root.style.setProperty('--mc-progress', p.toFixed(4));

      // Feather-width breathing: bell curve over progress, peaks at p=0.5.
      // 4 * p * (1-p) is the classic 0→1→0 curve with max 1.0 at 0.5.
      // Base 6% + up to 8% additional = 6%..14%..6% across the scroll.
      // Gives the feather its own rhythm so the reveal doesn't feel
      // mechanical; pairs with the parallax drift on the lattice itself.
      var bell = 4 * p * (1 - p);
      var featherPct = 6 + bell * 8;
      root.style.setProperty('--mc-feather', featherPct.toFixed(2) + '%');

      // Per-strand q
      for (var i = 0; i < strands.length; i++) {
        var el = strands[i];
        var start = el.__start, end = el.__end;
        var q;
        if (p <= start) q = 0;
        else if (p >= end) q = 1;
        else q = ease((p - start) / (end - start));
        el.style.setProperty('--q', q.toFixed(4));
      }

      // Phase-week readout — continuous rise/hold/peel-off (mobile; the labels
      // are display:none on desktop, so these writes are inert there).
      writeReadout(p);

      // Active beat
      var activeBeat = p < BEAT_EDGES[0] ? 0 : (p < BEAT_EDGES[1] ? 1 : 2);
      if (activeBeat !== lastBeat) {
        lastBeat = activeBeat;
        root.setAttribute('data-beat', String(activeBeat));
        for (var j = 0; j < beats.length; j++) {
          if (j === activeBeat) {
            beats[j].setAttribute('data-active', 'true');
          } else {
            beats[j].removeAttribute('data-active');
          }
        }
      }
    }

    // ── Pin-progress calculation ────────────────────────────────────────
    // The section is tall. Progress is measured as how far the viewport
    // top has traveled INTO the section, normalized by the section's
    // "pinnable" scroll distance (sectionHeight - viewport height).
    //
    // At p=0: section top is at viewport top (pin just engaged)
    // At p=1: section bottom is at viewport bottom (pin about to release)
    //
    // Before p=0: reader hasn't reached the section yet — we hold at 0.
    // After p=1: reader has scrolled past — we hold at 1.

    function computeProgress() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var rect = root.getBoundingClientRect();
      var sectionHeight = rect.height;

      // Scrollable distance during pin = sectionHeight - vh
      // Amount scrolled into section = -rect.top (clamped ≥ 0)
      var scrolled = -rect.top;
      var scrollable = sectionHeight - vh;

      if (scrollable <= 0) {
        // Section shorter than viewport — just show terminal state
        writeProgress(1);
        return;
      }

      var p = scrolled / scrollable;
      writeProgress(p);
    }

    // ── RAF throttle ────────────────────────────────────────────────────
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        computeProgress();
        ticking = false;
      });
    }

    // ── Mobile: scroll-driven progress on the scene element ───────────
    // On narrow viewports the section is a stacked, normal-height layout.
    // We drive --mc-progress from how far the scene has traveled through
    // the viewport (0 when scene bottom enters from below, 1 when scene
    // top has left from the top). Same visual result as the pinned path,
    // but with zero pin geometry — works under any mobile shell.
    var mql = window.matchMedia ? window.matchMedia('(max-width: 820px)') : null;
    var scene = root.querySelector('.mechanism__scene');

    function isMobile() {
      if (mql && mql.matches) return true;
      if (root.hasAttribute('data-force-mobile')) return true;
      return false;
    }

    function computeMobileProgress() {
      // Progress is strictly coupled to the SCROLL DISTANCE the lattice
      // has travelled up through the viewport. Every pixel of scroll
      // produces one pixel of reveal — no animation, no easing.
      //
      //   progress = 0 when the lattice TOP enters the viewport from below.
      //   progress = 1 after the lattice has travelled 0.85 × viewport
      //     upward — feels deliberate (slower than 1 flick) but finishes
      //     well before any realistic short-page scroll runs out.
      //
      // Travel window = 0.85 * vh. This used to be exactly vh (one full
      // viewport of scroll) but short layouts couldn't reach the top, so
      // the reveal never completed. 85% saturates reliably while still
      // feeling slow enough to read as intentional.
      var el = lattice || scene;
      if (!el) { writeProgress(0); return; }
      var vh = window.innerHeight || document.documentElement.clientHeight;
      if (vh <= 0) { writeProgress(0); return; }
      var rect = el.getBoundingClientRect();
      canvasH = rect.height; // keep the readout's die-travel synced to the live lattice height

      // Drive progress from the lattice's CENTER position within the
      // viewport. This is the most robust formulation for mobile because:
      //   (a) Progress=1 is always reachable regardless of page length —
      //       any user can scroll enough to put the lattice center in the
      //       upper half of the viewport.
      //   (b) The reveal plays out smoothly as the element moves; no
      //       thresholds, no snap-to-complete safety nets that cause
      //       violent jumps.
      //
      // Mapping:
      //   centerY = 0.85 * vh  (center just above bottom edge) → p = 0
      //   centerY = 0.30 * vh  (center clearly in upper third) → p = 1
      var centerY = rect.top + rect.height / 2;
      var startY  = vh * 0.85;
      var endY    = vh * 0.30;
      var span    = startY - endY;
      var p       = span > 0 ? (startY - centerY) / span : 0;
      writeProgress(p);
    }

    // rAF-based polling loop. We deliberately do NOT rely on scroll
    // events for the mobile reveal: in embedded preview iframes (and in
    // some mobile browser chrome configurations) scroll events don't
    // bubble to the frame's window. A continuous rAF tick reads the
    // lattice's live getBoundingClientRect each frame, so progress
    // always reflects the real on-screen position regardless of whether
    // scroll events fire. The writeProgress() short-circuit (same-value
    // skip) keeps this cheap when the page is idle.
    var mobileRafId = 0;
    function mobileTick() {
      computeMobileProgress();
      mobileRafId = window.requestAnimationFrame(mobileTick);
    }

    var mobileBound = false;
    function setupMobile() {
      if (mobileBound) return;
      mobileBound = true;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        writeProgress(1);
        return;
      }
      // Also keep scroll/resize bound as a fast path on platforms where
      // they do fire — writeProgress is idempotent so double-ticking is
      // harmless.
      window.addEventListener('scroll', mobileTick, { passive: true });
      window.addEventListener('resize', mobileTick, { passive: true });
      // Kick off the rAF loop.
      mobileTick();
    }

    function teardownMobile() {
      if (!mobileBound) return;
      mobileBound = false;
      window.removeEventListener('scroll', mobileTick);
      window.removeEventListener('resize', mobileTick);
      if (mobileRafId) {
        window.cancelAnimationFrame(mobileRafId);
        mobileRafId = 0;
      }
    }

    // ── IO gate: only listen while the section is near the viewport ────
    var active = false;
    var io = null;
    var desktopBound = false;

    function bindDesktop() {
      if (desktopBound) return;
      desktopBound = true;
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (!active) {
              active = true;
              window.addEventListener('scroll', onScroll, { passive: true });
              window.addEventListener('resize', onScroll, { passive: true });
            }
            computeProgress();
          } else {
            if (active) {
              active = false;
              window.removeEventListener('scroll', onScroll);
              window.removeEventListener('resize', onScroll);
            }
            // Clamp terminal state when scrolled past
            var rect = root.getBoundingClientRect();
            if (rect.bottom < 0) writeProgress(1);
            else if (rect.top > (window.innerHeight || 0)) writeProgress(0);
          }
        });
      }, { rootMargin: '200px 0px 200px 0px' });
      io.observe(root);
      computeProgress();
    }

    function unbindDesktop() {
      if (!desktopBound) return;
      desktopBound = false;
      if (io) { io.disconnect(); io = null; }
      if (active) {
        active = false;
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    }

    // Mode switch: desktop uses scroll-pin, mobile uses on-enter tween.
    function applyMode() {
      if (isMobile()) {
        unbindDesktop();
        setupMobile();
      } else {
        teardownMobile();
        bindDesktop();
      }
    }
    applyMode();
    if (mql && mql.addEventListener) {
      mql.addEventListener('change', applyMode);
    } else if (mql && mql.addListener) {
      // Safari < 14
      mql.addListener(applyMode);
    }
    // React when the root's data-force-mobile override flips (preview harness)
    if (typeof MutationObserver !== 'undefined') {
      var modeObs = new MutationObserver(applyMode);
      modeObs.observe(root, { attributes: true, attributeFilter: ['data-force-mobile'] });
    }

    // ── Phase dots: click to smooth-scroll to that phase ───────────────
    // Each dot carries data-target-progress (0..1). We invert the progress
    // formula:   p = (-rect.top) / (sectionHeight - vh)
    //         → scrollY_target = pageYOfSectionTop + p * (sectionHeight - vh)
    // where pageYOfSectionTop = window.scrollY + rect.top (current frame).
    var dots = Array.prototype.slice.call(root.querySelectorAll('.mechanism__dot'));

    function scrollToProgress(p) {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var rect = root.getBoundingClientRect();
      var sectionTopY = window.scrollY + rect.top;
      var scrollable = rect.height - vh;
      if (scrollable <= 0) return; // section fits in viewport — nothing to do
      var targetY = sectionTopY + Math.max(0, Math.min(1, p)) * scrollable;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: targetY,
        behavior: reduce ? 'auto' : 'smooth'
      });
    }

    function scrollToMobileProgress(p) {
      if (!scene) return;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var rect = scene.getBoundingClientRect();
      // Invert: p = (vh - rect.top) / (vh + rect.height)
      // → desired rect.top (relative to viewport) = vh - p*(vh + rect.height)
      var desiredTop = vh - Math.max(0, Math.min(1, p)) * (vh + rect.height);
      var deltaY = rect.top - desiredTop;
      var targetY = window.scrollY + deltaY;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: targetY,
        behavior: reduce ? 'auto' : 'smooth'
      });
    }

    dots.forEach(function (dot, idx) {
      dot.addEventListener('click', function () {
        var p = parseFloat(dot.getAttribute('data-target-progress'));
        if (isNaN(p)) return;
        if (isMobile()) {
          scrollToMobileProgress(p);
        } else {
          scrollToProgress(p);
        }
      });

      // Arrow-key navigation within the tablist
      dot.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = dots[idx + 1];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = dots[idx - 1];
        else if (e.key === 'Home') next = dots[0];
        else if (e.key === 'End')  next = dots[dots.length - 1];
        if (next) {
          e.preventDefault();
          next.focus();
          next.click();
        }
      });
    });

    // Keep aria-selected in sync with the active beat. Wrap the existing
    // beat-change path via a tiny observer on the root's data-beat attr.
    var ariaSync = new MutationObserver(function () {
      var b = parseInt(root.getAttribute('data-beat') || '0', 10);
      dots.forEach(function (d, i) {
        d.setAttribute('aria-selected', i === b ? 'true' : 'false');
      });
    });
    ariaSync.observe(root, { attributes: true, attributeFilter: ['data-beat'] });

    // Reduced motion: terminal state, disable scroll listener
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      writeProgress(1);
      root.setAttribute('data-beat', '2');
      beats.forEach(function (b) { b.setAttribute('data-active', 'true'); });
      unbindDesktop();
      teardownMobile();
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-mechanism]');
    for (var i = 0; i < roots.length; i++) initMechanism(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-init after a fetch-and-swap. The kiPdpSwap module fires
     `pdp:swapped` on `document` after replacing #main-content. The
     mechanism root from the previous page has been discarded along
     with its __mcInit flag and its rAF loop; the new root in its
     place needs initMechanism() called against it.

     boot()'s `__mcInit` guard means re-running it is safe — already-
     bound roots (none, after a swap, since they're freshly inserted
     nodes) are no-ops; new roots get bound.

     Why this matters mobile-specifically: the mobile reveal is
     entirely JS-driven (rAF loop reading getBoundingClientRect each
     frame). Without re-init, the new lattice sits in its initial
     state and never reveals. Desktop is less obviously broken
     because the sticky-pinned layout still looks reasonable static. */
  document.addEventListener('pdp:swapped', boot);
})();
